/**
 * Outbound provisioning — queries and mutations.
 *
 * Backed by internal/directory/provisioning.AdminHandler at
 * /api/v1/tenants/{tenant_id}/provisioning. A credential is sent once, in a
 * mutation, and never comes back: no response carries it, and nothing here
 * puts it in a cache.
 *
 * Step-up follows the server: changing where a connection points, who it
 * provisions or what it sends, starting it, and entering or withdrawing its
 * credential all prompt; pausing, disabling and testing never do.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import { withStepUp } from "@/lib/with-step-up";
import {
  armingPreviewSchema,
  failedDeliveriesSchema,
  connectionListSchema,
  connectionSchema,
  deliveriesSchema,
  obligationsSchema,
  probeResultSchema,
  targetPathsSchema,
  type ArmingPreview,
  type Connection,
  type ConnectionList,
  type FailedDelivery,
  type DeletePolicy,
  type Delivery,
  type Obligation,
  type MappingDraft,
  type ProbeResult,
  type ScopeKind,
  type TargetPaths,
} from "./schemas";

const base = (tenantID: string) => `/api/v1/tenants/${encodeURIComponent(tenantID)}/provisioning`;
const conn = (tenantID: string, id: string) => `${base(tenantID)}/connections/${encodeURIComponent(id)}`;

export const provisioningKeys = {
  all: (tenantID: string) => ["provisioning", tenantID] as const,
  list: (tenantID: string) => ["provisioning", tenantID, "connections"] as const,
  detail: (tenantID: string, id: string) => ["provisioning", tenantID, "connections", id] as const,
  targetPaths: (tenantID: string) => ["provisioning", tenantID, "target-paths"] as const,
  deliveries: (tenantID: string, id: string) => ["provisioning", tenantID, "connections", id, "deliveries"] as const,
  obligations: (tenantID: string, id: string) => ["provisioning", tenantID, "connections", id, "obligations"] as const,
  arming: (tenantID: string, id: string) => ["provisioning", tenantID, "connections", id, "arming"] as const,
  failed: (tenantID: string, id: string) => ["provisioning", tenantID, "connections", id, "failed"] as const,
};

export async function fetchConnections(tenantID: string): Promise<ConnectionList> {
  return connectionListSchema.parse(await apiFetch<unknown>(`${base(tenantID)}/connections`));
}

export async function fetchConnection(tenantID: string, id: string): Promise<Connection> {
  return connectionSchema.parse(await apiFetch<unknown>(conn(tenantID, id)));
}

/**
 * Polled while a connection is starting, because enumeration moves it to
 * active on its own; otherwise only re-read after this console changes it.
 */
export function useConnections(tenantID: string) {
  return useQuery<ConnectionList>({
    queryKey: provisioningKeys.list(tenantID),
    queryFn: () => fetchConnections(tenantID),
    refetchInterval: (q) => (q.state.data?.items.some((c) => c.state === "backfilling") ? 10_000 : false),
  });
}

export function useConnection(tenantID: string, id: string) {
  return useQuery<Connection>({
    queryKey: provisioningKeys.detail(tenantID, id),
    queryFn: () => fetchConnection(tenantID, id),
    refetchInterval: (q) => (q.state.data?.state === "backfilling" ? 10_000 : false),
  });
}

export function useTargetPaths(tenantID: string) {
  return useQuery<TargetPaths>({
    queryKey: provisioningKeys.targetPaths(tenantID),
    queryFn: async () => targetPathsSchema.parse(await apiFetch<unknown>(`${base(tenantID)}/target-paths`)),
    staleTime: Infinity,
  });
}

function useConnectionMutation<V>(
  tenantID: string,
  id: string,
  request: (vars: V) => Promise<unknown>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: V) => connectionSchema.parse(await request(vars)),
    onSuccess: (next) => {
      qc.setQueryData(provisioningKeys.detail(tenantID, id), next);
      void qc.invalidateQueries({ queryKey: provisioningKeys.list(tenantID) });
    },
  });
}

export function useCreateConnection(tenantID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { name: string; endpoint_url: string | null; scope_kind: ScopeKind }) =>
      connectionSchema.parse(
        await apiFetch<unknown>(`${base(tenantID)}/connections`, { method: "POST", body: vars }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: provisioningKeys.list(tenantID) }),
  });
}

export function useUpdateConnection(tenantID: string, id: string) {
  return useConnectionMutation(
    tenantID,
    id,
    (vars: {
      name?: string;
      endpoint_url?: string;
      scope_kind?: ScopeKind;
      delete_policy?: DeletePolicy;
      push_groups?: boolean;
      deprovision_deadline_minutes?: number;
      deprovision_ceiling?: number;
    }) =>
      withStepUp(() => apiFetch<unknown>(conn(tenantID, id), { method: "PATCH", body: vars })),
  );
}

/** Lets a connection that stopped at its deprovision ceiling deliver again. A fresh step-up. */
export function useLiftCeiling(tenantID: string, id: string) {
  return useConnectionMutation(tenantID, id, () =>
    withStepUp(() => apiFetch<unknown>(`${conn(tenantID, id)}/ceiling/lift`, { method: "POST" })),
  );
}

export function useSetGroups(tenantID: string, id: string) {
  return useConnectionMutation(tenantID, id, (groupIDs: string[]) =>
    withStepUp(() => apiFetch<unknown>(`${conn(tenantID, id)}/groups`, { method: "PUT", body: { group_ids: groupIDs } })),
  );
}

export function useSetAttributes(tenantID: string, id: string) {
  return useConnectionMutation(tenantID, id, (rows: MappingDraft[]) =>
    withStepUp(() =>
      apiFetch<unknown>(`${conn(tenantID, id)}/attributes`, { method: "PUT", body: { attributes: rows } }),
    ),
  );
}

/** The secret lives only in this call's arguments. */
export function useSetCredential(tenantID: string, id: string) {
  return useConnectionMutation(tenantID, id, (vars: { secret: string; auth_scheme: string }) =>
    withStepUp(() => apiFetch<unknown>(`${conn(tenantID, id)}/credential`, { method: "PUT", body: vars })),
  );
}

export function useWithdrawCredential(tenantID: string, id: string) {
  return useConnectionMutation(tenantID, id, () =>
    withStepUp(() => apiFetch<unknown>(`${conn(tenantID, id)}/credential`, { method: "DELETE" })),
  );
}

export type Transition = "activate" | "resume" | "pause" | "disable";

export function useTransition(tenantID: string, id: string) {
  return useConnectionMutation(tenantID, id, (to: Transition) => {
    const call = () => apiFetch<unknown>(`${conn(tenantID, id)}/${to}`, { method: "POST" });
    // Stopping a connection never waits on a ceremony.
    return to === "pause" || to === "disable" ? call() : withStepUp(call);
  });
}

export function useProbe(tenantID: string, id: string) {
  return useMutation<ProbeResult>({
    mutationFn: async () =>
      probeResultSchema.parse(await apiFetch<unknown>(`${conn(tenantID, id)}/probe`, { method: "POST" })),
  });
}

/**
 * The connection's recent delivery log. Polled every 15 seconds while the
 * connection is delivering, the same freshness as webhook logs; not polled when
 * nothing can be added to it.
 */
export function useDeliveries(tenantID: string, id: string, live: boolean) {
  return useQuery<Delivery[]>({
    queryKey: provisioningKeys.deliveries(tenantID, id),
    queryFn: async () => deliveriesSchema.parse(await apiFetch<unknown>(`${conn(tenantID, id)}/deliveries`)).items,
    refetchInterval: live ? 15_000 : false,
  });
}

/**
 * Leavers whose accounts at the target are not yet confirmed closed. Polled every
 * 30 seconds while any is open, the freshness of the recovery inbox: each closes
 * when a delivery reads the target back.
 */
export function useObligations(tenantID: string, id: string, anyOpen: boolean) {
  return useQuery<Obligation[]>({
    queryKey: provisioningKeys.obligations(tenantID, id),
    queryFn: async () => obligationsSchema.parse(await apiFetch<unknown>(`${conn(tenantID, id)}/obligations`)).items,
    refetchInterval: anyOpen ? 30_000 : false,
  });
}

/**
 * Leavers whose closure was recorded WITHOUT proof.
 *
 * A deprovision closes verified when the target is read back and the account is
 * gone. It also closes when the target answers the write but refuses the read —
 * a 403 from a narrowed token, a 405 or 501 from a target that cannot be read —
 * and that outcome is `unverified_2xx`: we were told it worked and could not
 * check. The console showed neither, so a connection with nothing open read as
 * "everyone who left has been confirmed closed", which is the one sentence a
 * security buyer audits and was not always true.
 *
 * Read on demand rather than polled: a closed obligation does not change.
 */
export function useUnverifiedClosures(tenantID: string, id: string) {
  return useQuery<Obligation[]>({
    queryKey: [...provisioningKeys.obligations(tenantID, id), "closed"],
    queryFn: async () =>
      obligationsSchema
        .parse(await apiFetch<unknown>(`${conn(tenantID, id)}/obligations?state=closed`))
        .items.filter((o) => o.state === "unverified_2xx"),
  });
}

/** What arming would do. Read on demand, never polled: it is reviewed, then restated. */
export function useArmingPreview(tenantID: string, id: string, enabled: boolean) {
  return useQuery<ArmingPreview>({
    queryKey: provisioningKeys.arming(tenantID, id),
    queryFn: async () => armingPreviewSchema.parse(await apiFetch<unknown>(`${conn(tenantID, id)}/arming`)),
    enabled,
    staleTime: 0,
  });
}

/** Arms a started connection, or activates a disabled one, restating the preview. */
export function useArm(tenantID: string, id: string, activate: boolean) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (digest: string) =>
      connectionSchema.parse(
        await withStepUp(() =>
          apiFetch<unknown>(`${conn(tenantID, id)}/${activate ? "activate" : "arm"}`, { method: "POST", body: { digest } }),
        ),
      ),
    onSuccess: (next) => {
      qc.setQueryData(provisioningKeys.detail(tenantID, id), next);
      void qc.invalidateQueries({ queryKey: provisioningKeys.list(tenantID) });
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: provisioningKeys.arming(tenantID, id) }),
  });
}

export function useFailedDeliveries(tenantID: string, id: string, enabled: boolean) {
  return useQuery<FailedDelivery[]>({
    queryKey: provisioningKeys.failed(tenantID, id),
    queryFn: async () => failedDeliveriesSchema.parse(await apiFetch<unknown>(`${conn(tenantID, id)}/failed`)).items,
    enabled,
  });
}

export function useRetryFailed(tenantID: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (itemIDs: string[]) =>
      (await apiFetch<{ retried: number }>(`${conn(tenantID, id)}/failed/retry`, { method: "POST", body: { item_ids: itemIDs } })).retried,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: provisioningKeys.failed(tenantID, id) });
      void qc.invalidateQueries({ queryKey: provisioningKeys.detail(tenantID, id) });
    },
  });
}

/** The tenant's kill switch. Never behind a ceremony. */
export function usePauseAll(tenantID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => connectionListSchema.parse(await apiFetch<unknown>(`${base(tenantID)}/pause-all`, { method: "POST" })).items,
    onSuccess: () => void qc.invalidateQueries({ queryKey: provisioningKeys.all(tenantID) }),
  });
}
